import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Stack,
  MagicWand,
  PencilSimple,
  Rows,
  SquaresFour,
  EyeSlash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { ColorChips } from "@/components/ColorChips";
import { BatchBar } from "@/components/BatchBar";
import { DensityToggle, useDensity } from "@/components/DensityToggle";
import { Thumb } from "@/components/Thumb";
import { WebsiteFlags } from "@/components/WebsiteFlags";
import { EnhanceAllDialog } from "@/components/EnhanceAllDialog";
import { api } from "@/lib/api";

const slabSqft = (s) => {
  const pieces = s.pieces || [];
  if (pieces.length)
    return (
      Math.round(
        pieces.reduce(
          (sum, p) =>
            sum +
            ((Number(p.length_in) || s.length_in || 0) *
              (Number(p.width_in) || s.width_in || 0)) /
              144,
          0,
        ) * 10,
      ) / 10
    );
  return (
    Math.round(((s.length_in * s.width_in) / 144) * (s.quantity || 1) * 10) / 10
  );
};

const FILTERS = [
  "All",
  "In Stock",
  "Out of Stock",
  "Reserved",
  "Consumed",
  "Completed",
  "Consignment",
  "Supplier stock",
  "Hidden",
];
const BADGE = {
  "In Stock": "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  "Out of Stock": "bg-red-500/10 text-red-400 border-red-500/25",
  Reserved: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  Consumed: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
};

export default function SlabsInventory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("All");
  const [primary, setPrimary] = useState("All");
  const [accent, setAccent] = useState("All");
  const [colorOptions, setColorOptions] = useState({ primary: [], accent: [] });
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [sort, setSort] = useState("Default");
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [density, setDensity, compact] = useDensity();
  const [view, setView] = useState(
    () => localStorage.getItem("bg_slabs_view") || "list",
  );
  useEffect(() => localStorage.setItem("bg_slabs_view", view), [view]);
  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filter === "Consignment") params.consignment = true;
    else if (filter === "Supplier stock") params.supplier_stock = true;
    else if (filter === "Completed") params.completed = true;
    else if (filter === "Hidden") params.hidden = true;
    else if (filter !== "All") params.status = filter;
    if (primary !== "All") params.primary_color = primary;
    if (accent !== "All") params.accent_color = accent;
    if (q.trim()) params.q = q.trim();
    const [list, st] = await Promise.all([
      api.get("/slabs", { params }),
      api.get("/slabs/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
    setLoading(false);
  }, [filter, primary, accent, q]);

  useEffect(() => {
    api
      .get("/slabs/colors")
      .then(({ data }) =>
        setColorOptions({
          primary: data.primary || [],
          accent: data.accent || [],
        }),
      )
      .catch(() => setColorOptions({ primary: [], accent: [] }));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const featureRank = (s) => (s.new_arrival ? 2 : 0) + (s.on_sale ? 1 : 0);
  const shown = useMemo(() => {
    const list = [...items];
    if (sort === "Website featured")
      list.sort(
        (a, b) =>
          featureRank(b) - featureRank(a) ||
          (a.slab_color || "").localeCompare(b.slab_color || ""),
      );
    else if (sort === "Color A–Z")
      list.sort((a, b) =>
        (a.slab_color || "").localeCompare(b.slab_color || ""),
      );
    return list;
  }, [items, sort]);
  const featuredCount = items.filter((s) => featureRank(s) > 0).length;
  const patchItem = (next) =>
    setItems((list) => list.map((s) => (s.id === next.id ? next : s)));

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Slabs" />
      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="slabs-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory / Slabs
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Slab Inventory
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Full slabs on hand by color, lot, rack and square footage
              </p>
            </div>
          </div>

          <div
            className="mt-5 hidden flex-wrap gap-2 border-t border-zinc-800 pt-4 sm:flex"
            data-testid="slabs-actions"
          >
            <Button
              data-testid="add-slab-button"
              onClick={() => navigate("/inventory/slabs/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Add Slabs
            </Button>
            <Button
              data-testid="slabs-report-button"
              variant="outline"
              onClick={() => navigate("/inventory/reporting")}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Reporting
            </Button>
            <Button
              data-testid="slabs-import-button"
              variant="outline"
              onClick={() => navigate("/settings/import-slabs")}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Import CSV
            </Button>
            <Button
              data-testid="slabs-enhance-all-button"
              variant="outline"
              onClick={() => setEnhanceOpen(true)}
              title="Give every older stock photo the HD treatment"
              className="h-12 border-emerald-800/60 bg-transparent px-4 text-[11px] uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-950/40"
            >
              <MagicWand size={14} weight="bold" className="mr-2" /> Enhance all photos
            </Button>
            {enhanceOpen && (
              <EnhanceAllDialog open={enhanceOpen} onClose={() => setEnhanceOpen(false)} />
            )}
          </div>
        </div>

        {stats && (
          <div className="mt-6 hidden grid-cols-2 gap-3 sm:grid sm:grid-cols-4">
            {[
              ["Slabs", stats.slabs, "text-white"],
              ["Total Sq Ft", stats.total_sqft, "text-yellow-400"],
              [
                "In Stock",
                `${stats.in_stock} / ${stats.in_stock_sqft} ft²`,
                "text-emerald-400",
              ],
              [
                "Reserved",
                `${stats.reserved} / ${stats.reserved_sqft} ft²`,
                "text-blue-400",
              ],
            ].map(([k, v, c]) => (
              <div
                key={k}
                data-testid={`slab-stat-${k.toLowerCase().replace(/ /g, "-")}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {k}
                </div>
                <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-start">
          <FilterBar
            className="min-w-0 flex-1"
            testidPrefix="slabs"
            search={{
              value: q,
              onChange: setQ,
              placeholder:
                "Search color, serial, barcode, lot, supplier, rack…",
              testid: "slab-search-input",
            }}
            sections={[
              {
                key: "sort",
                label: "Sort",
                chipPrefix: "Sort: ",
                value: sort,
                allValue: "Default",
                onChange: setSort,
                testid: "slab-sort-section",
                options: [
                  ["Default", "Default", "slab-sort-default"],
                  [
                    "Website featured",
                    `Website featured (${featuredCount})`,
                    "slab-sort-featured",
                  ],
                  ["Color A–Z", "Color A–Z", "slab-sort-color"],
                ],
              },
              {
                key: "stock",
                label: "Stock",
                value: filter,
                allValue: "All",
                onChange: setFilter,
                options: FILTERS.map((f) => [
                  f,
                  f,
                  `slab-filter-${f.toLowerCase().replace(/ /g, "-")}`,
                ]),
              },
              ...(colorOptions.primary.length
                ? [
                    {
                      key: "primary",
                      label: "Primary color",
                      chipPrefix: "Primary: ",
                      value: primary,
                      allValue: "All",
                      onChange: setPrimary,
                      testid: "slab-primary-filter-section",
                      options: [
                        ["All", "Any", "slab-primary-filter-all"],
                        ...colorOptions.primary.map((c) => [
                          c,
                          c,
                          `slab-primary-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
                        ]),
                      ],
                    },
                  ]
                : []),
              ...(colorOptions.accent.length
                ? [
                    {
                      key: "accent",
                      label: "Accent color",
                      chipPrefix: "Accent: ",
                      value: accent,
                      allValue: "All",
                      onChange: setAccent,
                      testid: "slab-accent-filter-section",
                      options: [
                        ["All", "Any", "slab-accent-filter-all"],
                        ...colorOptions.accent.map((c) => [
                          c,
                          c,
                          `slab-accent-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
                        ]),
                      ],
                    },
                  ]
                : []),
            ]}
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            <div className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800">
              {[
                ["list", Rows],
                ["grid", SquaresFour],
              ].map(([k, Icon]) => (
                <button
                  key={k}
                  data-testid={`slabs-view-${k}`}
                  onClick={() => setView(k)}
                  title={`${k} view`}
                  className={`flex h-12 items-center gap-2 px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    view === k
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {k}
                </button>
              ))}
            </div>
            {view === "list" && (
              <DensityToggle density={density} onChange={setDensity} />
            )}
          </div>
        </div>

        {view === "grid" && (
          <div
            className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="slab-grid"
          >
            {shown.map((s) => (
              <div
                key={s.id}
                className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-zinc-700"
              >
                <Link
                  to={`/inventory/slabs/${s.id}`}
                  data-testid={`slab-tile-${s.id}`}
                >
                  <Thumb
                    className="h-40"
                    item={{
                      ...s,
                      photos: (s.photos || []).filter((p) => !p.is_deleted)
                        .length
                        ? s.photos
                        : (s.pieces || []).flatMap((p) => p.photos || []),
                    }}
                    testId={`slab-thumb-${s.id}`}
                  />
                  <div className="flex items-start justify-between gap-2 p-3 pb-0">
                    <div className="min-w-0">
                      <div className="font-mono-data text-xs text-yellow-500">
                        {s.code}
                      </div>
                      <h3 className="truncate text-sm font-bold">
                        {s.slab_color}
                      </h3>
                      <ColorChips
                        primary={s.primary_color}
                        accent={s.accent_color}
                        testId={`slab-tile-colors-${s.id}`}
                        className="mt-1.5"
                      />
                      <div className="mt-1 text-xs text-zinc-400">
                        {s.length_in}" x {s.width_in}" · {s.thickness}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        BADGE[s.status] || BADGE["In Stock"]
                      }`}
                    >
                      {s.status}
                    </span>
                  </div>
                  {s.public_visible === false && (
                    <span
                      data-testid={`slab-tile-hidden-${s.id}`}
                      className="mt-2 inline-flex items-center gap-1 rounded-full border border-zinc-600 bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300"
                    >
                      <EyeSlash size={10} weight="bold" /> Hidden
                    </span>
                  )}
                </Link>
                {s.consumed_project_code && !s.consumed_project_archived && (
                  <Link
                    to={`/projects/${s.consumed_project_id}`}
                    data-testid={`slab-grid-consumed-link-${s.id}`}
                    className="mx-3 mt-2 inline-flex items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20"
                  >
                    Completed · {s.consumed_project_code}
                  </Link>
                )}
                <div className="mt-3 flex items-center justify-between border-t border-zinc-800 px-3 py-2">
                  <span className="font-mono-data text-xs text-yellow-400">
                    {s.quantity} × · {slabSqft(s)} ft²
                  </span>
                  <button
                    data-testid={`slab-grid-edit-${s.id}`}
                    onClick={() => navigate(`/inventory/slabs/${s.id}/edit`)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                  >
                    <PencilSimple size={13} weight="bold" /> Edit
                  </button>
                </div>
                <WebsiteFlags
                  slab={s}
                  prefix={`slab-grid-${s.id}`}
                  className="border-t border-zinc-800 px-3 py-2"
                  onSaved={patchItem}
                />
              </div>
            ))}
          </div>
        )}

        <div
          className={`mt-6 ${compact ? "space-y-1.5" : "space-y-3"} ${view === "grid" ? "hidden" : ""}`}
          data-testid="slab-list"
        >
          {loading &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900"
              />
            ))}
          {!loading && items.length === 0 && (
            <div
              data-testid="slab-empty-state"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            >
              <Stack
                size={28}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                No slabs in stock yet. Add your first bundle.
              </p>
            </div>
          )}
          {!loading &&
            shown.map((s) => {
              const total = slabSqft(s);
              return (
                <div
                  key={s.id}
                  className={`group flex items-center gap-3 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 pl-0 transition-all duration-150 hover:-translate-y-px hover:border-zinc-700 ${
                    compact ? "py-2 pr-3" : "py-4 pr-4"
                  }`}
                >
                  <span
                    className={`w-1 shrink-0 self-stretch ${
                      s.status === "In Stock"
                        ? "bg-emerald-400"
                        : s.status === "Reserved"
                          ? "bg-indigo-400"
                          : "bg-zinc-600"
                    }`}
                  />
                  <span className={compact ? "pl-2" : "pl-3"}>
                    <Checkbox
                      data-testid={`slab-select-${s.id}`}
                      checked={selected.includes(s.id)}
                      onCheckedChange={() => toggle(s.id)}
                      className="h-5 w-5 border-zinc-600 data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500 data-[state=checked]:text-white"
                    />
                  </span>
                  <Link
                    to={`/inventory/slabs/${s.id}`}
                    data-testid={`slab-card-${s.id}`}
                    className={`flex min-w-0 flex-1 items-center ${compact ? "gap-3" : "gap-4"}`}
                  >
                    <div
                      className={
                        compact
                          ? "flex min-w-0 flex-1 items-center gap-4"
                          : "min-w-0 flex-1"
                      }
                    >
                      <div className="font-mono-data text-xs uppercase tracking-widest text-yellow-500">
                        {s.code}
                      </div>
                      <h3
                        className={
                          compact
                            ? "truncate text-sm font-bold"
                            : "truncate text-lg font-bold"
                        }
                      >
                        {s.slab_color}
                      </h3>
                      <div
                        className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400 ${
                          compact ? "min-w-0 flex-1" : "mt-1 gap-x-5"
                        }`}
                      >
                        <span className="inline-flex items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5">
                          {s.material} · {s.finish}
                        </span>
                        <span className="hidden sm:inline-flex">
                          <ColorChips
                            primary={s.primary_color}
                            accent={s.accent_color}
                            testId={`slab-colors-${s.id}`}
                          />
                        </span>
                        <span className="font-mono-data inline-flex items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5">
                          {s.length_in}" x {s.width_in}" · {s.thickness}
                        </span>
                        <span className="hidden items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 sm:inline-flex">
                          <span className="text-zinc-600">Rack</span>
                          {s.location || "—"}
                        </span>
                        {s.serial_number && (
                          <span
                            data-testid={`slab-serial-${s.id}`}
                            className="hidden items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 sm:inline-flex"
                          >
                            <span className="text-zinc-600">SN</span>
                            {s.serial_number}
                          </span>
                        )}
                        {s.lot_number && (
                          <span className="hidden items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 sm:inline-flex">
                            <span className="text-zinc-600">Lot</span>
                            {s.lot_number}
                          </span>
                        )}
                        {s.stocked === false && (
                          <span
                            data-testid={`slab-nonstock-${s.id}`}
                            className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-400"
                          >
                            Supplier stock
                          </span>
                        )}
                        {s.consignment && (
                          <span
                            data-testid={`slab-consignment-${s.id}`}
                            className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-400"
                          >
                            Consignment
                          </span>
                        )}
                        {s.public_visible === false && (
                          <span
                            data-testid={`slab-hidden-${s.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-zinc-600 bg-zinc-800 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300"
                          >
                            <EyeSlash size={10} weight="bold" /> Hidden
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={
                        compact
                          ? "flex shrink-0 items-center gap-3"
                          : "text-right"
                      }
                    >
                      <div
                        className={`font-mono-data font-bold text-yellow-400 ${
                          compact ? "text-sm" : "text-lg"
                        }`}
                      >
                        {s.quantity} × · {total} ft²
                      </div>
                      <span
                        className={`inline-flex rounded-full border font-semibold uppercase tracking-wider ${
                          BADGE[s.status] || BADGE["In Stock"]
                        } ${compact ? "px-2 py-0.5 text-[10px]" : "mt-1 px-3 py-1 text-xs"}`}
                      >
                        {s.status}
                      </span>
                    </div>
                  </Link>
                  {s.consumed_project_code && !s.consumed_project_archived && (
                    <Link
                      to={`/projects/${s.consumed_project_id}`}
                      data-testid={`slab-consumed-link-${s.id}`}
                      className="hidden shrink-0 items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20 lg:inline-flex"
                    >
                      Completed · {s.consumed_project_code}
                    </Link>
                  )}
                  <WebsiteFlags
                    slab={s}
                    prefix={`slab-row-${s.id}`}
                    className="hidden w-44 shrink-0 lg:block"
                    onSaved={patchItem}
                  />
                  <button
                    data-testid={`slab-edit-${s.id}`}
                    onClick={() => navigate(`/inventory/slabs/${s.id}/edit`)}
                    title="Edit slab"
                    className="flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 text-[11px] font-semibold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300 sm:px-3"
                  >
                    <PencilSimple size={15} weight="bold" />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                </div>
              );
            })}
        </div>
      </main>

      <div className="no-print fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-800 bg-zinc-950 p-3 sm:hidden">
        <Button
          data-testid="add-slab-button-mobile"
          onClick={() => navigate("/inventory/slabs/new")}
          className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Plus size={18} weight="bold" className="mr-2" /> Add Slabs
        </Button>
      </div>

      <BatchBar
        selected={selected}
        onClear={() => setSelected([])}
        onDone={load}
        type="slabs"
      />
    </div>
  );
}
